// What each hint says, in English.
//
// **Content, not code** (§16.4). One file per locale beside the rules
// document, so a translator gets a file of sentences and nobody edits a `.tsx`
// to fix a comma. The curriculum decides *when* a concept is offered and knows
// none of these words; this file decides what it says and knows nothing about
// when.
//
// **The hints are an index into `content/rules/*.mdx`, never a second copy of
// it.** `line` is one imperative sentence and `detail` is a paragraph — what it
// does, what it costs, why you would. Anything longer belongs in the rules
// page, which every hint links to at its concept's `anchor`.
//
// **Any hint quoting a number reads it from `RULES`**, the same way `<Recipes/>`
// and `<Grades/>` do in the MDX, so a costed sentence cannot drift from the
// game. That is why this is a `.ts` and not an `.mdx` — the strings are keyed
// by concept id and looked up, rather than rendered as a document.

import { RULES } from "src/engine/rules/rules";
import { topMetal } from "src/engine/rules/materials";
import type { MechanicId } from "src/engine/telemetry/mechanics";

export interface HintText {
  /** One imperative line, on the chip. */
  line: string;
  /** A paragraph, on hover. */
  detail: string;
}

export const HINTS: Partial<Record<MechanicId, HintText>> = {
  mine: {
    line: "Stand a pawn on a scrap heap to mine it.",
    detail:
      `Captures leave scrap where the piece died. A pawn standing on a heap ` +
      `digs ${RULES.minerRate} out of it a turn, by itself, every turn it ` +
      `stays there. Nothing is pinned to a heap — you can walk away and come ` +
      `back — and the pile is finite, so a worked-out square is done.`
  },
  handoff: {
    line: "Pass what a pawn is holding on to a belt or a machine.",
    detail:
      `A miner is the bottom of the chain, not a link in it: it digs and hands ` +
      `on, and takes delivery from nothing. It will pass to a machine it is ` +
      `touching before a belt beside it. This happens on its own each turn — ` +
      `the button is there so you can watch it happen.`
  },
  belts: {
    line: "Drag out a belt to carry ore where you want it.",
    detail:
      `Each part costs ${RULES.beltCost} cobble and is built at once, on a bare ` +
      `square beside one of your pieces. It costs no turn. Your own pieces ` +
      `cross and stand on your track; an enemy plows through it, and a part ` +
      `it breaks spills its cargo as scrap.`
  },
  "belt-aim": {
    line: "Click a belt to turn it — aiming is free.",
    detail:
      `A part carries what is on it one square a turn, the way it points. ` +
      `Re-aiming costs nothing, is not bound to your turn, and can be done as ` +
      `often as you like — so a misdrawn run costs the metal it took to build ` +
      `and nothing else. Lifting a part gives its iron back.`
  },
  refine: {
    line: "A bishop, queen or king cooks ore one grade higher.",
    detail:
      `Cooking takes a turn and changes grade, never quantity. A unit may ` +
      `never be cooked twice by the same machine, so reaching the top of the ` +
      `metal line takes ${topMetal()} different ovens. Your king is always one of ` +
      `them, which is what guarantees the economy can never dead-end. Stone ` +
      `cooks free; metal burns a coal first, which takes the oven's tick.`
  },
  recipe: {
    line: "Tell a rook or your king what to save up for.",
    detail:
      `A foundry keeps the metal it can use toward the piece you name and ` +
      `passes the rest along. Every recipe costs more than the piece returns, ` +
      `which is the only place material ever leaves the economy — so what you ` +
      `choose to build is what the whole factory has been for.`
  },
  forge: {
    line: "Your foundry has what it needs — mint the piece.",
    detail:
      `Forging spends your turn, like a move. The new piece lands touching ` +
      `the foundry that made it, and a minted pawn faces away from it. Your ` +
      `king can forge too, but it may not wall itself in doing so.`
  },
  "re-aim": {
    line: "A knight can turn a pawn to face a new way.",
    detail:
      `A pawn moves and captures the way it faces, not the way its army ` +
      `marches — so a pawn pointing the wrong way is a pawn out of the fight. ` +
      `A knight spends its whole turn to re-aim one a knight's move away, or ` +
      `one it is touching. It cannot do it while you are in check.`
  }
};
